/**
 * TND-013 — la vue transverse « Révisions de prix » : UNE requête, lue par la
 * page ET par la barre latérale (qui masque l'entrée tant que la liste est vide).
 * Même clé, même cache : le menu et la page ne peuvent pas se contredire.
 *
 * Le filtre par état est appliqué à l'ÉCRAN, sur la liste entière : le pied
 * « 3 marchés à revaloriser — … » porte sur tout le portefeuille, jamais sur le
 * filtre en cours.
 */

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { crmKeys } from '@/lib/queryKeys.crm'

import type { PriceRevisionsCount, PriceRevisionsResponse } from './usePriceRevisions.types'

/** Le chemin, écrit UNE fois : le gardien de couture le compare à la route serveur. */
export const PRICE_REVISIONS_PATH = '/api/crm/framework-contracts/price-revisions'

/**
 * L'adresse d'export de la VUE AFFICHÉE (`exportEndpoint` de la page) : le menu
 * « Exporter » y poste les identifiants des lignes de l'écran, dans son ordre, et
 * le CENTRE D'EXPORT écrit le fichier. Écrite une fois, comme la liste : un gardien
 * la confronte aux routes POST montées (`test_table_export_routes_derivees.py`).
 */
export const PRICE_REVISIONS_EXPORT_PATH = '/api/crm/framework-contracts/price-revisions/export'

export function usePriceRevisions(entityId: string, { enabled = true }: { enabled?: boolean } = {}) {
    /* La LANGUE part avec la requête : le serveur écrit lui-même les cellules de
       texte (indices, formule). Elle entre dans la clé, sous le préfixe de l'entité
       — les invalidations par `priceRevisions(entityId)` l'atteignent toujours. */
    const { i18n } = useTranslation()
    const langue = i18n?.language || 'fr'
    return useQuery<PriceRevisionsResponse>({
        queryKey: [...crmKeys.frameworkContracts.priceRevisions(entityId), langue],
        queryFn: async () => {
            const params = new URLSearchParams({ entity_id: entityId, lang: langue })
            const res = await apiFetch(`${PRICE_REVISIONS_PATH}?${params.toString()}`)
            if (!res.ok) await handleApiError(res)
            return res.json() as Promise<PriceRevisionsResponse>
        },
        enabled: enabled && Boolean(entityId),
        staleTime: 60_000,
    })
}

/** Le chemin du COMPTE, écrit UNE fois — le gardien de couture le compare à la route. */
export const PRICE_REVISIONS_COUNT_PATH = '/api/crm/framework-contracts/price-revisions/count'

/**
 * Le COMPTE seul, pour la BARRE LATÉRALE.
 *
 * 🔴 Pourquoi il existe. L'entrée de menu se masque tant que la liste est vide,
 * et la barre latérale posait donc la question à la LISTE ELLE-MÊME — sur chaque
 * écran, toutes les 60 secondes. Tant que la vue ne servait que les contrats
 * portant une clause de révision, cela ne coûtait rien. Depuis qu'elle sert AUSSI
 * le secteur privé, c'est tout le portefeuille de contrats cadres qui transitait
 * en permanence : mesuré à ~1 Ko par contrat, soit ~1,8 Mo par minute et par
 * écran à 2 000 contrats. Aucune erreur, aucune lenteur imputable — juste une
 * application qui s'alourdit à mesure que le client réussit.
 *
 * 🔑 L'invariant TENU : le menu reste piloté par la LISTE. Le compte ne recompte
 * rien de son côté — il partage littéralement le `FROM … WHERE` de la requête de
 * la liste (`_DEPUIS_LES_CONTRATS`), et un gardien refuse qu'ils divergent.
 */
export function usePriceRevisionsCount(
    entityId: string,
    { enabled = true }: { enabled?: boolean } = {},
) {
    return useQuery<PriceRevisionsCount>({
        queryKey: crmKeys.frameworkContracts.priceRevisionsCount(entityId),
        queryFn: async () => {
            const res = await apiFetch(
                `${PRICE_REVISIONS_COUNT_PATH}?entity_id=${encodeURIComponent(entityId)}`,
            )
            if (!res.ok) await handleApiError(res)
            return res.json() as Promise<PriceRevisionsCount>
        },
        enabled: enabled && Boolean(entityId),
        staleTime: 60_000,
    })
}
